import { useEffect, useLayoutEffect } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);
gsap.defaults({ ease: "expo.out", duration: 0.9 });

export { gsap, ScrollTrigger };

export const useIsoLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;

export function prefersReducedMotion() {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/* Heavy effects (WebGL, smooth scroll) are progressive enhancement: skip them on
   reduced motion, data-saver, or very low-end devices. */
export function canRunHeavyMotion() {
  if (typeof window === "undefined") return false;
  if (prefersReducedMotion()) return false;
  const nav = window.navigator || {};
  if (nav.connection && (nav.connection.saveData || /(^|-)2g$/.test(nav.connection.effectiveType || ""))) return false;
  if (nav.hardwareConcurrency && nav.hardwareConcurrency <= 2) return false;
  if (nav.deviceMemory && nav.deviceMemory <= 2) return false;
  return true;
}

/**
 * Scoped GSAP setup with automatic cleanup. `setup(ctx)` runs inside a
 * gsap.context bound to `scopeRef`, so selectors are local and every tween /
 * ScrollTrigger it creates is reverted on unmount or when `deps` change.
 * Skipped entirely under reduced motion: content is already visible by default.
 */
export function useGsap(setup, deps, scopeRef, { allowReduced = false } = {}) {
  useIsoLayoutEffect(() => {
    if (!scopeRef.current) return undefined;
    if (!allowReduced && prefersReducedMotion()) return undefined;
    const ctx = gsap.context((self) => setup(self), scopeRef);
    return () => ctx.revert();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

/* Lenis singleton so anchors / route changes can drive the smooth scroller. */
let lenisInstance = null;
export const setLenis = (l) => {
  lenisInstance = l;
};
export const getLenis = () => lenisInstance;

export function scrollToTarget(target, { offset = -72, immediate = false } = {}) {
  const el = typeof target === "string" ? document.querySelector(target) : target;
  if (!el && target !== 0) return;
  const lenis = getLenis();
  if (lenis) {
    lenis.scrollTo(el ?? 0, { offset, immediate, duration: 1.2 });
  } else if (el) {
    const y = el.getBoundingClientRect().top + window.scrollY + offset;
    window.scrollTo({ top: y, behavior: immediate || prefersReducedMotion() ? "auto" : "smooth" });
  } else {
    window.scrollTo({ top: 0, behavior: "auto" });
  }
}

/* Split a heading into word spans for staggered reveals. Splits on spaces only,
   so Arabic letters keep their joining and CJK runs stay intact. */
export function splitWords(text) {
  return String(text)
    .split(/(\s+)/)
    .filter((w) => w.length);
}
